import { useRouteLoaderData } from "@remix-run/react";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FeaturedBadge,
  ServiceCardSubtitle,
  ServiceInfoButton,
  getFeaturedIconId,
  getServiceCardTexts,
} from "~/components/booking/flow/ServiceCardExtras";
import {
  cn,
  getCmsServiceCategoryFromList,
  getCmsServiceFromList,
} from "~/lib/utils";
import { sortByWpCategoryOrder } from "~/lib/wp-order";

interface ServiceCategorySelectProps {
  availabilities: any[];
  guestsCount: { male: number; female: number };
  onSkip: (availability: any) => void;
  onSelect: (availability: any) => void;
  selectedValue: any;
}

const ServiceCategorySelect = ({
  availabilities,
  guestsCount,
  onSkip,
  onSelect,
  selectedValue,
}: ServiceCategorySelectProps) => {
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const [normalAvailabilities, setNormalAvailabilities] = useState<any[]>([]);

  useEffect(() => {
    const filteredAvailabilities = availabilities.filter((availability) => {
      if (
        availability.availability_woman === 0 &&
        availability.availability_man === 0 &&
        availability.availability_general === 0
      ) {
        return false;
      }
      if (
        availability.availability_woman < guestsCount.female &&
        availability.availability_man < guestsCount.male &&
        availability.availability_general <
        guestsCount.male + guestsCount.female
      ) {
        return false;
      }
      const cmsService = getCmsServiceFromList(
        rootData.servicesData,
        availability.category,
      );
      const cmsServiceCategory = getCmsServiceCategoryFromList(
        rootData.normalServiceCategories,
        availability.category,
      );
      return !!cmsService || !!cmsServiceCategory;
    });

    if (filteredAvailabilities.length === 1) {
      onSkip(filteredAvailabilities[0]);
    } else {
      // Categorie nell'ordine di WordPress, non in quello dell'API.
      setNormalAvailabilities(
        sortByWpCategoryOrder(
          filteredAvailabilities,
          (availability: any) => availability.category,
          rootData.normalServiceCategories,
        ),
      );
    }
  }, [availabilities]);

  const renderCategorySection = (
    availabilities: any[],
    isUpselling: boolean = false,
  ) => {
    if (availabilities.length === 0) return null;

    return (
      <div className="w-full">
        <div
          className={cn(
            "w-full grid grid-cols-1 lg:grid-cols-2 items-stretch gap-4",
          )}
        >
          {availabilities.map((availability: any, index: number) => {
            const cmsServiceCategory = getCmsServiceCategoryFromList(
              isUpselling
                ? rootData.upsellingServiceCategories
                : rootData.normalServiceCategories,
              availability.category,
            );
            if (!cmsServiceCategory) return null;

            const servicesForCategory = availabilities.filter(
              (avail: any) => avail.category === availability.category,
            );
            const uniqueServices = new Set(
              servicesForCategory.map((avail: any) => {
                return avail.service || avail.code || avail.category;
              }),
            );
            const hasSingleService = uniqueServices.size === 1;

            const serviceCode = hasSingleService
              ? availability.service ||
              availability.code ||
              availability.category
              : null;

            const cmsService =
              hasSingleService && serviceCode
                ? getCmsServiceFromList(rootData.servicesData, serviceCode)
                : null;

            const displayName =
              hasSingleService && cmsService
                ? (() => {
                  const serviceCode =
                    availability.service ||
                    availability.code ||
                    availability.category;
                  const serviceCodeData = cmsService.acf?.codes?.find(
                    (c: any) => c.code === serviceCode,
                  );
                  return (
                    cmsService.acf?.title ||
                    serviceCodeData?.short_description ||
                    cmsServiceCategory?.name
                  );
                })()
                : cmsServiceCategory?.name;

            const displayImage =
              hasSingleService && cmsService?.acf?.image?.id
                ? cmsService.acf.image.id
                : cmsServiceCategory.acf?.image?.id;

            const { subtitle: cardSubtitle, description: cardDescription } =
              hasSingleService && cmsService
                ? getServiceCardTexts(cmsService, serviceCode)
                : { subtitle: null, description: null };

            return (
              <div
                key={`${isUpselling ? "upselling" : "normal"}-${index}`}
                className={cn(
                  "w-full h-full rounded-2xl overflow-hidden bg-slate-200 cursor-pointer relative flex flex-col",
                  {
                    "bg-white ring-2 ring-secondary":
                      selectedValue?.category === availability.category,
                  },
                )}
                onClick={() => onSelect(availability)}
              >
                <ServiceInfoButton
                  title={displayName}
                  description={cardDescription}
                  imageId={displayImage}
                />
                <div className="w-full h-52 relative shrink-0">
                  <img
                    alt=""
                    src={`/cms-asset/${displayImage}`}
                    className="w-full h-full object-cover object-center z-10"
                  />
                  {/* The VIP tab takes the corner, the price moves above it. */}
                  <div
                    className={cn(
                      "absolute right-4 bg-secondary rounded-2xl",
                      getFeaturedIconId(rootData, cmsService, serviceCode)
                        ? "bottom-10"
                        : "bottom-4",
                    )}
                  >
                    <span className="text-secondary-foreground block px-2 py-0.5">
                      €
                      {availability.minprice &&
                        availability.minprice?.toLocaleString(i18n.language)}
                    </span>
                  </div>
                  <FeaturedBadge
                    cmsService={cmsService}
                    code={serviceCode}
                    onImage
                    className="absolute bottom-0 -right-0.5"
                  />
                </div>
                <div className="p-4 flex-1">
                  <span>{displayName}</span>
                  {hasSingleService &&
                    cmsService &&
                    (() => {
                      const serviceCode =
                        availability.service ||
                        availability.code ||
                        availability.category;
                      const serviceCodeData = cmsService.acf?.codes?.find(
                        (c: any) => c.code === serviceCode,
                      );
                      return serviceCodeData?.short_description &&
                        serviceCodeData.short_description !== displayName ? (
                        <span className="text-sm text-gray-500 block mt-1">
                          {serviceCodeData.short_description}
                        </span>
                      ) : null;
                    })()}
                  <ServiceCardSubtitle text={cardSubtitle} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      id="step-service-select"
      className="w-full max-w-2xl mx-auto flex flex-col"
    >
      <h3 className="font-bold text-xl text-center">
        {t("Tipologia di servizio")}
      </h3>
      <p className="text-center mt-2">
        {t("Seleziona la tipologia di servizio")}
      </p>

      <div className="mt-8 space-y-8">
        {normalAvailabilities.length === 0 && (
          <div className="flex items-center justify-center">
            <AlertTriangle size={24} className="text-white mx-auto mb-2" />
            <span className="text-white text-center block">
              {t("Si è verificato un errore durante la ricerca dei servizi")}
            </span>
            <span className="text-white text-center block text-xs">
              {t("Contattaci per ottenere assistenza")}
            </span>
          </div>
        )}

        {renderCategorySection(normalAvailabilities, false)}
      </div>
    </div>
  );
};

export default ServiceCategorySelect;
